import Letterhead from "../Reusable/Letterhead2";
import MetaInfo from "../Reusable/MetaInfo2";
import SubjectLine from "../Reusable/SubjectLine2";
import Signature from "../Reusable/Signature2";

interface DeviceAllocationLetterProps {
  refNo: string;
  date: string;
  name: string;
  address: string;
  emp_id: string;
  device_id: string;
}

function DeviceAllocationLetter({
  name,
  refNo,
  date,
  address,
  emp_id,
  device_id,
}: DeviceAllocationLetterProps) {
  return (
    <div
      className="max-w-4xl mx-auto p-6 bg-white text-gray-800 leading-relaxed text-[17px]"
      style={{ fontFamily: "Times New Roman" }}
    >
      <Letterhead />
      <hr className="mx-auto w-4/4 border-t-2 border-black" />
      <MetaInfo refNo={refNo} date={date} name={name} address={address} />
      <div className="flex flex-row justify-center items-center">
      <SubjectLine subject="Device Allocation Letter" />
      <p className="mt-[-15px] ml-[10px]">(confidential)</p>
 </div>
      <p className="mb-2">
        Dear <b>{name}</b>,
      </p>

      <p className="mb-4">
        We are pleased to inform you that we have provided you with the following devices
        for performing your job with us. We look forward to your smooth journey with NHWM
        and hope you are excited about this opportunity!
      </p>

      <h2 className="text-[17px] font-semibold mt-6 mb-2">
        Devices Handed Over To You
      </h2>
      <div className="pl-5 space-y-1">
        <ol className="list-decimal">
          <li>One set of BCA machine with Charger Cable</li>
          <li>One set of Automatic BP Monitoring system</li>
          <li>
            One set of Accu-Check Glucometer with 60 Test Strips, 100 Lancet and 100 Alcohol
            Prep Pads
          </li>
          <li>One set of Measuring Tape</li>
          <li>One set of Presentation Flip Chart</li>
          <li>One set of NHWM branded bag</li>
        </ol>
      </div>

      <div className="mt-8">
        <p>Yours faithfully,</p>
        <p className="font-semibold mt-2">Dr. Monideep Boruah</p>
        <p>Mission Director</p>
        <p>National Healthcare and Wellness Mission</p>
        <p className="mt-2 text-gray-600">
          An Initiative of Optim Health and Wellness (OPC) Pvt. Ltd.<br />
          16, R K Kakati Path, Hengrabari, Guwahati - 36
        </p>
      </div>

      <div className="mt-[400px]">
        <br/>
        <h2 className="text-[17px] font-semibold mt-8 mb-3 flex justify-center items-center">
          Terms and Conditions for Devices
        </h2>

        <div className="space-y-4">
          <div>
            <p className="font-semibold">1. Purpose</p>
            <p>
              These Terms and Conditions outline the rules, responsibilities and obligations
              governing the use, handling, and return of medical devices assigned by{" "}
              <b>NHWM (National Healthcare Wellness Mission)</b> to employees for official
              duties, training, or clinical operations.
              <br />
              All employees must read, understand, and comply with the provisions mentioned
              herein.
            </p>
          </div>

          <div>
            <p className="font-semibold">2. Scope</p>
            <p>
              This policy applies to all employees, trainees, consultants, and contractual
              staff of NHWM who are issued medical devices or equipment for official purposes.
            </p>
          </div>

          <div>
            <p className="font-semibold">3. Ownership</p>
            <p>
              All medical devices and related accessories remain the{" "}
              <b>sole property of NHWM.</b> Employees are custodians of the equipment and
              responsible for its proper usage, care, and security during the period of
              assignment.
            </p>
          </div>

          <div>
            <p className="font-semibold">4. Assignment and Use</p>
            <ul className="list-disc list-inside">
              <li>
                Devices are assigned strictly for <b>official and authorized purposes</b> only.
              </li>
              <li>Personal or non-work-related use is strictly prohibited.</li>
              <li>
                Employees must ensure that devices are operated only by qualified and trained
                personnel.
              </li>
              <li>
                Devices must be used in accordance with the manufacturer’s guidelines and
                NHWM’s internal safety protocols.
              </li>
            </ul>
          </div>

          <div>
            <p className="font-semibold">5. Responsibility and Care</p>
            <ul className="list-disc list-inside">
              <li>
                The assigned employee is responsible for the <b>safe handling, maintenance, and
                protection</b> of the device.
              </li>
              <li>
                Any <b>loss, theft, or damage</b> must be reported to the concerned department
                (Biomedical/HR/Administration).
              </li>
              <li>
                Negligence, misuse, or unauthorized modification of the device may result in
                disciplinary action and <b>financial liability.</b>
              </li>
            </ul>
          </div>

          <div>
            <p className="font-semibold">6. Maintenance and Servicing</p>
            <ul className="list-disc list-inside">
              <li>
                Routine maintenance and calibration will be carried out by the NHWM Biomedical
                Department or an authorized service provider.
              </li>
              <li>Employees must not attempt self-repair or servicing of devices.</li>
              <li>
                Any malfunction must be promptly reported to the designated technical authority.
              </li>
            </ul>
          </div>

          <div>
            <p className="font-semibold">7. Return Policy</p>
            <ol className="list-disc list-inside">
              <li>Devices must be returned in good working condition upon:</li>
              <ol className="list-[lower-alpha] pl-6 space-y-1 list-inside">
                <li>Completion of the assigned project or role.</li>
                <li>Resignation, transfer, or termination of employment.</li>
                <li>When requested by the NHWM authority.</li>
              </ol>

              <li>
                Failure to return the assigned device, or returning it in a damaged or
                non-functional state (beyond normal wear and tear), will result in <b>deduction from
                final settlement</b> or <b>legal action</b> as per NHWM policy.
              </li>
            </ol>
          </div>
          <br/>
          <div>
          <br/>
            <p className="font-semibold">8. Data Protection and Confidentiality</p>
            <ul className="list-disc list-inside">
              <li>
                Devices containing patient data, medical records, or proprietary information must
                be handled in strict <b>confidentiality.</b>
              </li>
              <li>
                Employees must ensure <b>no unauthorized access</b>, copying, or sharing of data from
                assigned medical devices.
              </li>
              <li>
                All data must be cleared or transferred to NHWM’s secure system before return.
              </li>
            </ul>
          </div>

          <div>
            <p className="font-semibold">9. Loss, Theft, or Damage</p>
            <ul className="list-disc list-inside">
              <li>
                In case of loss or theft, a written report must be submitted immediately to HR
                and Administration.
              </li>
              <li>
                NHWM reserves the right to recover the cost of the device from the employee if
                the incident is determined to be due to negligence or misconduct.
              </li>
            </ul>
          </div>

          <div>
            <p className="font-semibold">10. Inspection and Audit</p>
            <p>
              NHWM reserves the right to <b>inspect, audit, or recall</b> any assigned medical
              device at any time to ensure compliance, safety, and functionality.
            </p>
          </div>

          <div>
            <p className="font-semibold">11. Disciplinary Actions</p>
            <p>
              Violation of any of these terms, including misuse, negligence, or non-compliance,
              may lead to:
            </p>
            <ul className="list-disc list-inside">
              <li>Written warning</li>
              <li>Suspension of device privileges</li>
              <li>Recovery of damages</li>
              <li>
                Disciplinary or legal action under NHWM’s Code of Conduct and HR policy
              </li>
            </ul>
          </div>

          <div>
            <p className="font-semibold">12. Employee Acknowledgement</p>
            <p className="px-8">
              All employees receiving NHWM medical devices must sign the acknowledgement below:
            </p>
            <h3 className="font-semibold mt-2">Acknowledgement:</h3>
            <p>
              I, <b>{name}</b>, Employee ID: <b>{emp_id}</b>, acknowledge that I have received the
              assigned medical device (Device Name/ID: <b>{device_id}</b>). I have read,
              understood, and agree to comply with all the above Terms and Conditions. I
              understand that failure to do so may result in disciplinary and/or financial
              consequences as per NHWM policy.
            </p>
            <p className="mt-4">Signature: _____________________</p>
            <p>Date: __________________________</p>
            <p>Department: ____________________</p>
            <p>Authorized By (NHWM): ____________________</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeviceAllocationLetter;
